'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { RateConfig } from './OnboardingWizard';
import { IndianRupee, Info } from 'lucide-react';

interface Step3RatesProps {
  rates: RateConfig;
  onChange: (r: RateConfig) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function Step3Rates({ rates, onChange, onNext, onBack }: Step3RatesProps) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RateConfig>({
    defaultValues: rates,
  });

  const hourly = watch('hourlyRate') || 0;
  const session = watch('sessionRate') || 0;
  const rush = watch('rushMultiplier') || 1.5;

  const onSubmit = (data: RateConfig) => {
    onChange(data);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="surface-elevated p-6 flex flex-col gap-5">
        <div>
          <h2 className="font-heading text-xl text-foreground mb-1">Set Your Rates</h2>
          <p className="text-sm text-muted-foreground">
            Rates are shown publicly in INR. You can update these later from your dashboard.
          </p>
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="hourlyRate">
            Hourly Rate
          </label>
          <p className="text-xs text-muted-foreground mb-2">Your standard per-hour engagement rate in INR.</p>
          <div className="relative">
            <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="hourlyRate"
              type="number"
              step="500"
              min="1000"
              {...register('hourlyRate', {
                required: 'Hourly rate is required',
                min: { value: 1000, message: 'Minimum rate is ₹1,000/hr' },
                max: { value: 200000, message: 'Maximum rate is ₹2,00,000/hr' },
                valueAsNumber: true,
              })}
              className="w-full bg-input border border-border rounded-lg pl-8 pr-4 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors font-mono-data"
              placeholder="e.g. 25000"
              aria-describedby="hourlyRate-error"
            />
          </div>
          {errors.hourlyRate && (
            <p id="hourlyRate-error" className="text-xs text-red-400 mt-1">{errors.hourlyRate.message}</p>
          )}
          {hourly > 0 && (
            <p className="text-xs text-muted-foreground mt-1 font-mono-data">
              ₹{Number(hourly).toLocaleString('en-IN')}/hr
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="sessionRate">
            Session Rate
          </label>
          <p className="text-xs text-muted-foreground mb-2">Per 2-hour block session rate in INR.</p>
          <div className="relative">
            <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="sessionRate"
              type="number"
              step="500"
              min="2000"
              {...register('sessionRate', {
                required: 'Session rate is required',
                min: { value: 2000, message: 'Minimum session rate is ₹2,000' },
                valueAsNumber: true,
              })}
              className="w-full bg-input border border-border rounded-lg pl-8 pr-4 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors font-mono-data"
              placeholder="e.g. 45000"
              aria-describedby="sessionRate-error"
            />
          </div>
          {errors.sessionRate && (
            <p id="sessionRate-error" className="text-xs text-red-400 mt-1">{errors.sessionRate.message}</p>
          )}
          {session > 0 && hourly > 0 && (
            <p className="text-xs text-muted-foreground mt-1 font-mono-data">
              {session < hourly * 2 ? '⚠ Below 2× hourly rate' : `${((session / (hourly * 2)) * 100 - 100).toFixed(0)}% vs 2× hourly`}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="rushMultiplier">
            Rush Multiplier
          </label>
          <p className="text-xs text-muted-foreground mb-2">Applied to rate when urgency = URGENT. Default 1.5×.</p>
          <input
            id="rushMultiplier"
            type="number"
            step="0.1"
            min="1.0"
            max="3.0"
            {...register('rushMultiplier', {
              required: 'Rush multiplier is required',
              min: { value: 1.0, message: 'Minimum multiplier is 1.0' },
              max: { value: 3.0, message: 'Maximum multiplier is 3.0' },
              valueAsNumber: true,
            })}
            className="w-full bg-input border border-border rounded-lg px-4 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors font-mono-data"
            placeholder="1.5"
            aria-describedby="rushMultiplier-error"
          />
          {errors.rushMultiplier && (
            <p id="rushMultiplier-error" className="text-xs text-red-400 mt-1">{errors.rushMultiplier.message}</p>
          )}
        </div>

        {hourly > 0 && (
          <div className="rounded-lg bg-muted/50 border border-border p-4">
            <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Rate Summary</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Hourly', value: `₹${Number(hourly).toLocaleString('en-IN')}` },
                { label: 'Session (2hr)', value: `₹${Number(session).toLocaleString('en-IN')}` },
                { label: 'Rush Rate', value: `₹${Math.round(Number(hourly) * Number(rush)).toLocaleString('en-IN')}/hr` },
              ].map((item) => (
                <div key={`rate-summary-${item.label}`} className="text-center">
                  <div className="font-mono-data text-sm font-semibold text-foreground">{item.value}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-secondary/5 border border-secondary/15">
          <Info size={14} className="text-secondary flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            These rates are displayed on your public profile. Buyers see them before requesting an engagement. Agreed rates are set per engagement and may differ.
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-6 rounded-lg border border-border text-muted-foreground font-medium text-sm hover:border-primary/30 hover:text-foreground active:scale-95 transition-all duration-150"
        >
          Back
        </button>
        <button
          type="submit"
          className="flex-1 py-3 px-6 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150"
        >
          Preview Profile
        </button>
      </div>
    </form>
  );
}